import { createContext, useCallback, useContext, useMemo } from 'react';
import type { FunctionComponent } from 'react';

import { SBOM_PORTAL_PRIVATE } from '../../../../shared/featureFlags/flagNames';
import { useAsyncRequest } from '../../../hooks';
import { getPortalFeatures } from '../../api/organization';
import useSlug from '../../hooks/useSlug';

export const context = createContext<{ isPrivatePortalEnabled: boolean; isLoading: boolean }>({
  isPrivatePortalEnabled: false,
  isLoading: false,
});

const useIsPrivatePortalEnabled = () => {
  const isPrivatePortalEnabledContext = useContext(context);
  if (!isPrivatePortalEnabledContext) {
    throw new Error('useIsPrivatePortalEnabled must be used within a IsPrivatePortalEnabledProvider');
  }

  return isPrivatePortalEnabledContext;
};

export const Provider: FunctionComponent = ({ children }) => {
  const slug = useSlug();
  const getReleaseGroupsReq = useCallback(() => getPortalFeatures(slug), [slug]);
  const { value, isLoading } = useAsyncRequest(getReleaseGroupsReq);
  const contextValue = useMemo(
    () => ({ isPrivatePortalEnabled: (value?.features || []).includes(SBOM_PORTAL_PRIVATE), isLoading }),
    [value, isLoading],
  );

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};

export default useIsPrivatePortalEnabled;
